import React, { useEffect, useRef } from 'react';
import { Box, Typography, Container, Paper, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DotmanImagePage: React.FC = () => {
   const navigate = useNavigate();
   const canvasRef = useRef<HTMLCanvasElement>(null);

   useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas size - large for high-quality screenshots
      canvas.width = 800;
      canvas.height = 800;

      // Clear canvas with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Center of canvas
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Draw Dotman character (larger scale)
      const size = 1000; // Large size for screenshot
      const radius = size / 2;

      // Save context
      ctx.save();
      ctx.translate(centerX, centerY);

      // Render Dotman facing right (good default view)
      const direction = 'right' as const;
      const isMoving = true; // Show walking animation
      const isSuperMode = false;

      // Rotate character to face movement direction (facing right)
      const rotationAngle = Math.PI / 2; // Rotate 90 degrees clockwise (head points right)

      ctx.rotate(rotationAngle);

      // Walking animation cycle
      const walkCycle = -Math.sin(Date.now() / 150) * (isMoving ? 1 : 0);

      // Head (yellow) - positioned at the back
      ctx.fillStyle = '#FFFF00'; // Yellow head
      ctx.beginPath();
      ctx.arc(0, radius * 0.3, radius * 0.4, 0, Math.PI * 2);
      ctx.fill();

      // Add highlight on head for 3D effect
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.beginPath();
      ctx.arc(-radius * 0.1, radius * 0.4, radius * 0.15, 0, Math.PI * 2);
      ctx.fill();

      // Body/torso (blue shirt) - positioned at center
      ctx.fillStyle = '#4169E1'; // Blue body
      ctx.beginPath();
      ctx.ellipse(0, -radius * 0.1, radius * 0.35, radius * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();

      // Legs - positioned at the front with walking animation
      const legRadius = radius * 0.15;
      const legSpread = radius * 0.4;
      const walkOffset = walkCycle * radius * 0.15;

      // Determine leg positions - facing right direction
      const leftLegX = -legSpread;
      const leftLegY = -radius * 0.5 + (isMoving ? walkOffset : 0);
      const rightLegX = legSpread;
      const rightLegY = -radius * 0.5 - (isMoving ? walkOffset : 0);

      // Draw legs (brown)
      ctx.fillStyle = '#8B4513';

      // Left leg
      ctx.beginPath();
      ctx.arc(leftLegX, leftLegY, legRadius, 0, Math.PI * 2);
      ctx.fill();

      // Right leg
      ctx.beginPath();
      ctx.arc(rightLegX, rightLegY, legRadius, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();

      // Optional: Add text label below
      //  ctx.fillStyle = '#FFFFFF';
      //  ctx.font = 'bold 48px Arial';
      //  ctx.textAlign = 'center';
      //  ctx.fillText('Dotman', centerX, canvas.height - 50);
   }, []);

   return (
      <Box
         sx={{
            minHeight: '100vh',
            height: '100vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            background: 'linear-gradient(135deg, rgb(10, 12, 22) 0%, rgb(44, 26, 61) 100%)',
            padding: 4,
         }}
      >
         <Container maxWidth="lg">
            <Box sx={{ mb: 3 }}>
               <Button
                  startIcon={<ArrowBack />}
                  onClick={() => navigate('/')}
                  variant="outlined"
                  sx={{ mb: 2 }}
               >
                  Back to Home
               </Button>
               <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
                  Dotman Character Image
               </Typography>
               <Typography variant="body1" sx={{ color: '#CCCCCC', mb: 3 }}>
                  Large view of the Dotman character for screenshots. The character is displayed on a transparent background.
               </Typography>
            </Box>

            <Paper
               sx={{
                  padding: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
               }}
            >
               <Box
                  sx={{
                     backgroundColor: 'rgba(0, 0, 0, 0.3)',
                     borderRadius: 2,
                     padding: 2,
                     display: 'inline-block',
                  }}
               >
                  <canvas
                     ref={canvasRef}
                     style={{
                        display: 'block',
                        maxWidth: '100%',
                        height: 'auto',
                     }}
                  />
               </Box>
               <Typography variant="body2" sx={{ color: '#CCCCCC', mt: 2, textAlign: 'center' }}>
                  Right-click on the image and select "Save image as..." to download
               </Typography>
            </Paper>
         </Container>
      </Box>
   );
};

export default DotmanImagePage;

